import { useMemo, useState, useRef, useEffect, type ElementType } from 'react'
import { useStudioStore } from '@/store/studio-store'
import { useEntityIndex } from '../hooks/use-entity-index'
import { useDataValidation } from '../hooks/use-data-validation'
import { VariableTable } from './tables/variable-table'
import { DatasourceTable } from './tables/datasource-table'
import { EventTable } from './tables/event-table'
import { FlowTable } from './tables/flow-table'
import { DataRelationshipGraph } from './data-relationship-graph'
import { CreateVariableForm } from './create-variable-form'
import { CreateDatasourceForm } from './create-datasource-form'
import { CreateEventForm } from './create-event-form'
import { CreateFlowForm } from '@/features/flow-editor/components/create-flow-form'
import { QuickTemplates } from './quick-templates'
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover'
import {
  exportCategory,
  exportAllData,
  importCategory,
  importAllData,
} from '../services/data-io'
import {
  Search,
  X,
  Download,
  Upload,
  ChevronDown,
  Variable,
  Database,
  Zap,
  GitBranch,
  Package,
  Plus,
  AlertCircle,
  AlertTriangle,
  LayoutGrid,
  GitFork,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import type { DataCategory } from '@/store/types'
import { useStudioLocale } from '@/i18n/use-studio-locale'
import { getDataSurfaceCopy } from '@/features/data-explorer/data-surface-copy'

// Identity + icon only. Every LABEL below is locale-derived at render time
// from `data-surface-copy.ts`, shared with `shell/ide-v2/surfaces/data-surface.tsx`
// so the navigator and this canvas cannot name the same category differently.
const IO_CATEGORIES: { id: DataCategory; icon: ElementType }[] = [
  { id: 'variables', icon: Variable },
  { id: 'datasources', icon: Database },
  { id: 'events', icon: Zap },
  { id: 'flows', icon: GitBranch },
]

const IMPORT_CATEGORIES = IO_CATEGORIES.filter(({ id }) => id !== 'flows')

const TABS: { id: DataCategory }[] = [
  { id: 'variables' },
  { id: 'datasources' },
  { id: 'events' },
  { id: 'flows' },
]

// Categories that support creation
const CREATABLE_CATEGORIES: DataCategory[] = ['variables', 'datasources', 'events', 'flows']

function getStateScopeBucket(scope: string): 'appData' | 'local' | 'block' {
  const normalized = scope.toLowerCase()
  if (normalized === 'appdata' || normalized === 'app' || normalized === 'global') return 'appData'
  if (normalized === 'local' || normalized === 'localstate') return 'local'
  return 'block'
}

function StateScopePill({ label, value }: { label: string; value: number }) {
  return (
    <span
      data-testid={`data-state-scope-${label.toLowerCase()}`}
      className="inline-flex h-6 items-center gap-1.5 rounded-md border border-border bg-background px-2 text-[11px] text-muted-foreground"
    >
      <span>{label}</span>
      <span className="font-mono font-semibold text-foreground">{value}</span>
    </span>
  )
}

function DataMetric({
  id,
  label,
  value,
  Icon,
}: {
  id: DataCategory
  label: string
  value: number
  Icon: ElementType
}) {
  return (
    <div
      data-testid={`data-state-count-${id}`}
      className="flex min-w-0 items-center justify-between gap-2 rounded-md border border-border bg-muted/20 px-2.5 py-1.5"
    >
      <div className="flex min-w-0 items-center gap-1.5">
        <Icon size={13} className="shrink-0 text-muted-foreground" />
        <span className="truncate text-[11px] text-muted-foreground">{label}</span>
      </div>
      <span className="font-mono text-xs font-semibold text-foreground">{value}</span>
    </div>
  )
}

function DataEmptyState({
  category,
  searchQuery,
  scopeCounts,
  onCreate,
}: {
  category: DataCategory
  searchQuery: string
  scopeCounts: { appData: number; local: number; block: number }
  onCreate: () => void
}) {
  const { locale } = useStudioLocale()
  const copy = getDataSurfaceCopy(locale)
  const isFiltered = searchQuery.trim().length > 0
  const scopeDetail = `appData ${scopeCounts.appData} / local ${scopeCounts.local} / block ${scopeCounts.block}`
  return (
    <div
      data-testid="data-empty-state"
      className="flex min-h-[220px] flex-col items-center justify-center gap-3 px-4 text-center"
    >
      <div>
        <div className="text-sm font-semibold text-foreground">
          {isFiltered ? copy.noMatchingEntities : copy.emptyTitle[category]}
        </div>
        <div className="mt-1 font-mono text-xs text-muted-foreground">
          {isFiltered ? searchQuery : scopeDetail}
        </div>
      </div>
      {!isFiltered && CREATABLE_CATEGORIES.includes(category) && (
        <button
          type="button"
          onClick={onCreate}
          className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-background px-2.5 text-xs font-medium text-foreground hover:bg-muted"
        >
          <Plus size={13} />
          {copy.addAction[category]}
        </button>
      )}
    </div>
  )
}

export function DataCanvas({
  showCategoryTabs = true,
  visibleCategories = TABS.map((tab) => tab.id),
}: {
  showCategoryTabs?: boolean
  visibleCategories?: readonly DataCategory[]
} = {}) {
  const project = useStudioStore((s) => s.currentProject)
  const projectName = useStudioStore((s) => s.currentProject?.manifest?.name ?? 'project')
  const dataCategory = useStudioStore((s) => s.dataCategory)
  const setDataCategory = useStudioStore((s) => s.setDataCategory)
  const searchQuery = useStudioStore((s) => s.searchQuery)
  const setSearchQuery = useStudioStore((s) => s.setSearchQuery)
  const index = useEntityIndex()
  const validationMessages = useDataValidation(index, project)
  const { locale } = useStudioLocale()
  const copy = getDataSurfaceCopy(locale)

  const [viewMode, setViewMode] = useState<'table' | 'graph'>('table')
  const [showSearch, setShowSearch] = useState(false)
  const [exportOpen, setExportOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const visibleTabs = TABS.filter((tab) => visibleCategories.includes(tab.id))
  const visibleIoCategories = IO_CATEGORIES.filter((category) =>
    visibleCategories.includes(category.id),
  )
  const visibleImportCategories = IMPORT_CATEGORIES.filter((category) =>
    visibleCategories.includes(category.id),
  )

  useEffect(() => {
    if (showSearch) inputRef.current?.focus()
  }, [showSearch])

  function handleToggleSearch() {
    const next = !showSearch
    setShowSearch(next)
    if (!next) setSearchQuery('')
  }

  const filtered = useMemo(() => {
    if (!searchQuery) return index
    const q = searchQuery.toLowerCase()
    return {
      ...index,
      variables: index.variables.filter((v) => v.name.toLowerCase().includes(q) || v.blockName.toLowerCase().includes(q)),
      datasources: index.datasources.filter((d) => d.name.toLowerCase().includes(q) || d.blockName.toLowerCase().includes(q)),
      events: index.events.filter((e) => e.eventKey.toLowerCase().includes(q) || e.blockName.toLowerCase().includes(q)),
      flows: index.flows.filter((f) => f.name.toLowerCase().includes(q)),
    }
  }, [index, searchQuery])

  const blocks = project ? Object.keys(project.blocks) : []
  const defaultBlock = blocks[0] ?? ''
  const stateScopeCounts = useMemo(() => {
    return index.variables.reduce(
      (acc, variable) => {
        acc[getStateScopeBucket(variable.scope)] += 1
        return acc
      },
      { appData: 0, local: 0, block: 0 },
    )
  }, [index.variables])

  // Validation summary counts
  const errorCount = validationMessages.filter((m) => m.level === 'error').length
  const warningCount = validationMessages.filter((m) => m.level === 'warning').length
  const activeItems = filtered[dataCategory]
  const activeEmpty = viewMode === 'table' && activeItems.length === 0

  if (!project) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
        {copy.noProjectLoaded}
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      {/* Category tabs + view toggle + search */}
      <div className="flex items-center gap-1 px-3 py-2 border-b border-border">
        {/* View mode toggle */}
        <div className="flex items-center mr-2 border-r border-border pr-2">
          <button
            onClick={() => setViewMode('table')}
            className={cn(
              'flex h-6 w-6 items-center justify-center rounded transition-colors',
              viewMode === 'table'
                ? 'text-foreground bg-accent'
                : 'text-muted-foreground hover:text-foreground hover:bg-accent/50',
            )}
            title={copy.tableView}
          >
            <LayoutGrid size={13} />
          </button>
          <button
            onClick={() => setViewMode('graph')}
            className={cn(
              'flex h-6 w-6 items-center justify-center rounded transition-colors',
              viewMode === 'graph'
                ? 'text-foreground bg-accent'
                : 'text-muted-foreground hover:text-foreground hover:bg-accent/50',
            )}
            title={copy.graphView}
          >
            <GitFork size={13} />
          </button>
        </div>

        {/* Category tabs (table mode only) */}
        {viewMode === 'table' && showCategoryTabs && visibleTabs.map((tab) => (
          <button
            key={tab.id}
            data-testid={`data-tab-${tab.id}`}
            className={cn(
              'px-3 py-1 text-xs rounded transition-colors',
              dataCategory === tab.id
                ? 'bg-accent text-accent-foreground font-medium'
                : 'text-muted-foreground hover:text-foreground hover:bg-accent/50',
            )}
            onClick={() => {
              setDataCategory(tab.id)
              setShowCreateForm(false)
            }}
          >
            {copy.categoryLabel[tab.id]}
          </button>
        ))}

        <div className="flex-1" />

        {/* Table mode controls */}
        {viewMode === 'table' && (
          <>
            {/* Add button (only for creatable categories) */}
            {CREATABLE_CATEGORIES.includes(dataCategory) && (
              <button
                data-testid="data-add-entity-button"
                onClick={() => setShowCreateForm((v) => !v)}
                className={cn(
                  'flex h-6 w-6 items-center justify-center rounded transition-colors',
                  showCreateForm
                    ? 'text-primary bg-primary/10'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent/50',
                )}
                title={copy.addAction[dataCategory]}
              >
                {showCreateForm ? <X size={13} /> : <Plus size={13} />}
              </button>
            )}

            {/* Export dropdown */}
            <Popover open={exportOpen} onOpenChange={setExportOpen}>
              <PopoverTrigger asChild>
                <button className="flex h-6 items-center gap-1 rounded px-1.5 text-[11px] text-muted-foreground hover:bg-accent hover:text-foreground transition-colors">
                  <Download size={12} />
                  <span>{copy.export}</span>
                  <ChevronDown size={10} />
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" sideOffset={6} className="w-44">
                {visibleIoCategories.map(({ id, icon: Icon }) => (
                  <button
                    key={id}
                    className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs hover:bg-accent transition-colors"
                    onClick={() => {
                      exportCategory(id, index[id])
                      setExportOpen(false)
                    }}
                  >
                    <Icon size={13} className="text-muted-foreground" />
                    {copy.categoryLabel[id]}
                  </button>
                ))}
                <div className="my-1 h-px bg-border" />
                <button
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs font-medium hover:bg-accent transition-colors"
                  onClick={() => {
                    exportAllData(index, projectName)
                    setExportOpen(false)
                  }}
                >
                  <Package size={13} className="text-muted-foreground" />
                  {copy.allData}
                </button>
              </PopoverContent>
            </Popover>

            {/* Import dropdown */}
            <Popover open={importOpen} onOpenChange={setImportOpen}>
              <PopoverTrigger asChild>
                <button className="flex h-6 items-center gap-1 rounded px-1.5 text-[11px] text-muted-foreground hover:bg-accent hover:text-foreground transition-colors">
                  <Upload size={12} />
                  <span>{copy.import}</span>
                  <ChevronDown size={10} />
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" sideOffset={6} className="w-44">
                {visibleImportCategories.map(({ id, icon: Icon }) => (
                  <button
                    key={id}
                    className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs hover:bg-accent transition-colors"
                    onClick={() => {
                      importCategory(id)
                      setImportOpen(false)
                    }}
                  >
                    <Icon size={13} className="text-muted-foreground" />
                    {copy.categoryLabel[id]}
                  </button>
                ))}
                <div className="my-1 h-px bg-border" />
                <button
                  className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs font-medium hover:bg-accent transition-colors"
                  onClick={() => {
                    importAllData()
                    setImportOpen(false)
                  }}
                >
                  <Package size={13} className="text-muted-foreground" />
                  {copy.allData}
                </button>
              </PopoverContent>
            </Popover>

            {showSearch && (
              <input
                data-testid="data-filter-input"
                ref={inputRef}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={copy.filterPlaceholder}
                className="h-6 w-32 rounded border border-border bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
              />
            )}
            <button
              data-testid="data-filter-toggle"
              onClick={handleToggleSearch}
              className={cn(
                'flex h-6 w-6 items-center justify-center rounded transition-colors',
                showSearch
                  ? 'text-foreground bg-accent'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent/50',
              )}
              title={copy.filterEntities}
            >
              {showSearch ? <X size={13} /> : <Search size={13} />}
            </button>
          </>
        )}
      </div>

      <section
        data-testid="data-state-readiness"
        className="grid gap-3 border-b border-border bg-background px-3 py-2 lg:grid-cols-[minmax(210px,0.8fr)_minmax(360px,1.2fr)]"
      >
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-2">
            <Database size={14} className="shrink-0 text-primary" />
            <div className="truncate text-xs font-semibold text-foreground">{copy.stateModel}</div>
            {(errorCount > 0 || warningCount > 0) && (
              <span className="rounded-md border border-border bg-muted/30 px-1.5 py-0.5 text-[10px] text-muted-foreground">
                {errorCount > 0 ? copy.errorCount(errorCount) : copy.warningCount(warningCount)}
              </span>
            )}
          </div>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            <StateScopePill label="appData" value={stateScopeCounts.appData} />
            <StateScopePill label="local" value={stateScopeCounts.local} />
            <StateScopePill label="block" value={stateScopeCounts.block} />
          </div>
        </div>
        <div className="grid min-w-0 grid-cols-2 gap-1.5 md:grid-cols-4">
          {visibleCategories.includes("variables") && (
            <DataMetric id="variables" label={copy.metricLabel.variables} value={index.summary.variables} Icon={Variable} />
          )}
          {visibleCategories.includes("datasources") && (
            <DataMetric id="datasources" label={copy.metricLabel.datasources} value={index.summary.datasources} Icon={Database} />
          )}
          {visibleCategories.includes("events") && (
            <DataMetric id="events" label={copy.metricLabel.events} value={index.summary.events} Icon={Zap} />
          )}
          {visibleCategories.includes("flows") && (
            <DataMetric id="flows" label={copy.metricLabel.flows} value={index.summary.flows} Icon={GitBranch} />
          )}
        </div>
      </section>

      {/* Creation form (inline, above the table — table mode only) */}
      {viewMode === 'table' && showCreateForm && (
        <>
          {dataCategory === 'variables' && (
            <CreateVariableForm
              defaultBlockName={defaultBlock}
              onClose={() => setShowCreateForm(false)}
            />
          )}
          {dataCategory === 'datasources' && (
            <CreateDatasourceForm
              defaultBlockName={defaultBlock}
              onClose={() => setShowCreateForm(false)}
            />
          )}
          {dataCategory === 'events' && (
            <CreateEventForm
              defaultBlockName={defaultBlock}
              onClose={() => setShowCreateForm(false)}
            />
          )}
          {dataCategory === 'flows' && (
            <CreateFlowForm onClose={() => setShowCreateForm(false)} />
          )}
          {/* Quick templates */}
          {defaultBlock && dataCategory !== 'flows' && (
            <div className="px-3 py-1.5 border-b border-border">
              <QuickTemplates blockName={defaultBlock} />
            </div>
          )}
        </>
      )}

      {/* Main content area */}
      <div className="flex-1 overflow-auto">
        {viewMode === 'table' ? (
          activeEmpty ? (
            <DataEmptyState
              category={dataCategory}
              searchQuery={searchQuery}
              scopeCounts={stateScopeCounts}
              onCreate={() => setShowCreateForm(true)}
            />
          ) : (
            <>
              {dataCategory === 'variables' && <VariableTable variables={filtered.variables} />}
              {dataCategory === 'datasources' && <DatasourceTable datasources={filtered.datasources} />}
              {dataCategory === 'events' && <EventTable events={filtered.events} />}
              {dataCategory === 'flows' && <FlowTable flows={filtered.flows} />}
            </>
          )
        ) : (
          <DataRelationshipGraph />
        )}
      </div>

      {/* Status bar */}
      <div className="px-3 py-1.5 border-t border-border text-[10px] text-muted-foreground flex items-center gap-3">
        {visibleCategories.includes("variables") && (
          <span>{copy.statusVariables(index.summary.variables)}</span>
        )}
        {visibleCategories.includes("datasources") && (
          <span>{copy.statusSources(index.summary.datasources)}</span>
        )}
        {visibleCategories.includes("events") && (
          <span>{copy.statusEvents(index.summary.events)}</span>
        )}
        {visibleCategories.includes("flows") && (
          <span>{copy.statusFlows(index.summary.flows)}</span>
        )}
        {(errorCount > 0 || warningCount > 0) && (
          <>
            <span className="mx-1">|</span>
            {errorCount > 0 && (
              <span className="flex items-center gap-0.5 text-destructive">
                <AlertCircle size={10} /> {errorCount}
              </span>
            )}
            {warningCount > 0 && (
              <span className="flex items-center gap-0.5 text-yellow-600">
                <AlertTriangle size={10} /> {warningCount}
              </span>
            )}
          </>
        )}
        <span className="ml-auto">{copy.statusBlocks(index.blocks.length)}</span>
      </div>
    </div>
  )
}
